import React from 'react';
import { X, Edit } from 'lucide-react';
import { Employee } from '../../../types/employee';

interface EditEmployeeModalProps {
  employee: Employee | null;
  isOpen: boolean;
  onClose: () => void;
  onSave: (employee: Employee) => void;
}

export const EditEmployeeModal: React.FC<EditEmployeeModalProps> = ({
  employee,
  isOpen,
  onClose,
  onSave,
}) => {
  if (!isOpen || !employee) return null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget as HTMLFormElement);
    const updatedEmployee = {
      ...employee,
      nome: formData.get('nome') as string,
      telefone: formData.get('telefone') as string,
      status: formData.get('status') as string,
    };
    onSave(updatedEmployee);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 animate-[fadeIn_0.1s_ease-out_forwards]">
      <div 
        className="bg-white shadow-lg border border-slate-200 rounded-lg overflow-hidden max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto animate-[slideInFromBottom2_0.15s_ease-out_forwards] animate-[zoomIn95_0.15s_ease-out_forwards]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header do Modal */}
        <div className="relative bg-gradient-to-r from-teal-500 to-teal-600 text-white p-4 animate-[slideInFromTop2_0.15s_ease-out_forwards]">
          <div className="absolute top-0 left-0 right-0 h-0.5 bg-white/30"></div>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 bg-white/20 rounded-lg flex items-center justify-center">
                <Edit className="w-6 h-6 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white animate-[fadeIn_0.2s_ease-out_forwards]">Editar Funcionário</h2>
                <p className="text-sm text-teal-100 animate-[fadeIn_0.25s_ease-out_forwards]">Atualize os dados do funcionário</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-white/80 hover:text-white hover:bg-white/20 rounded-lg transition-colors animate-[fadeIn_0.3s_ease-out_forwards]"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
        
        {/* Formulário */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Nome Completo *</label>
              <input
                type="text"
                name="nome"
                defaultValue={employee.nome}
                required
                className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all duration-200"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Data de Nascimento</label>
              <input
                type="date"
                name="dataNascimento"
                defaultValue={employee.dataNascimento || ''}
                className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all duration-200"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Telefone</label>
              <input
                type="text"
                name="telefone"
                defaultValue={employee.telefone || ''}
                className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all duration-200"
                placeholder="(11) 99999-9999"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Status *</label>
              <select
                name="status"
                defaultValue={employee.status}
                required
                className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all duration-200"
              >
                <option value="ativo">🟢 Ativo</option>
                <option value="inativo">🔴 Inativo</option>
              </select>
            </div>
          </div>
          
          {/* Botões de Ação */}
          <div className="flex justify-end gap-4 pt-6 border-t border-slate-200">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-3 text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors font-medium"
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="px-6 py-3 bg-gradient-to-r from-teal-500 to-teal-600 text-white rounded-lg hover:from-teal-600 hover:to-teal-700 transition-all duration-200 font-medium shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
            >
              <Edit className="w-4 h-4 inline mr-2" />
              Salvar Alterações
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
